import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const InstituteManagementPage = ({ instituteType, instituteName }) => {
  const [institute, setInstitute] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [basicInfoForm, setBasicInfoForm] = useState({
    name: '',
    short_name: '',
    hero_image: '',
    about_description: '',
    mandates: [''],
    statistics: [{ label: '', value: '', icon: 'Users' }],
    contact_address: '',
    contact_phone: '',
    contact_email: '',
    key_activities: [''],
    hero_banners: [''],
    gallery_images: [''],
    linkages_content: '',
    linkages_logos: []
  });

  const [projectForm, setProjectForm] = useState({
    title: '',
    funding_agency: '',
    duration: '',
    status: 'ongoing',
    serial_number: 0
  });

  const [activityForm, setActivityForm] = useState({
    title: '',
    description: '',
    activity_date: '',
    image_url: ''
  });

  const [achievementForm, setAchievementForm] = useState({
    title: '',
    description: '',
    year: '',
    image_url: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch institute
      const instituteRes = await axios.get(`${API_URL}/api/institutes/${instituteType}`);
      setInstitute(instituteRes.data);
      setBasicInfoForm({
        name: instituteRes.data.name || '',
        short_name: instituteRes.data.short_name || '',
        hero_image: instituteRes.data.hero_image || '',
        about_description: instituteRes.data.about_description || '',
        mandates: instituteRes.data.mandates?.length > 0 ? instituteRes.data.mandates : [''],
        statistics: instituteRes.data.statistics?.length > 0 ? instituteRes.data.statistics : [{ label: '', value: '', icon: 'Users' }],
        contact_address: instituteRes.data.contact_address || '',
        contact_phone: instituteRes.data.contact_phone || '',
        contact_email: instituteRes.data.contact_email || '',
        key_activities: instituteRes.data.key_activities?.length > 0 ? instituteRes.data.key_activities : [''],
        hero_banners: instituteRes.data.hero_banners?.length > 0 ? instituteRes.data.hero_banners : [''],
        gallery_images: instituteRes.data.gallery_images?.length > 0 ? instituteRes.data.gallery_images : [''],
        linkages_content: instituteRes.data.linkages_content || '',
        linkages_logos: instituteRes.data.linkages_logos || []
      });

      // Fetch projects
      const projectsRes = await axios.get(`${API_URL}/api/institutes/${instituteType}/projects`);
      setProjects(projectsRes.data);

      // Fetch activities
      const activitiesRes = await axios.get(`${API_URL}/api/institutes/${instituteType}/activities`);
      setActivities(activitiesRes.data);

      // Fetch achievements
      const achievementsRes = await axios.get(`${API_URL}/api/institutes/${instituteType}/achievements`);
      setAchievements(achievementsRes.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const uploadFiles = async () => {
    const uploadedUrls = {};
    const uploadPromises = [];

    // Identify files to upload
    for (const [key, files] of Object.entries(fileData)) {
      const fileList = Array.isArray(files) ? files : [files];

      for (const file of fileList) {
        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);

          const promise = axios.post(`${API_URL}/api/files/upload?subfolder=institutes`, formData, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }).then(response => {
            const url = `/api/files/${response.data.id}`;
            if (!uploadedUrls[key]) uploadedUrls[key] = [];
            uploadedUrls[key].push(url);
          });

          uploadPromises.push(promise);
        }
      }
    }

    if (uploadPromises.length > 0) {
      toast.info(`Uploading ${uploadPromises.length} files...`);
      await Promise.all(uploadPromises);
    }

    return uploadedUrls;
  };

  const saveBasicInfo = async () => {
    try {
      setSaving(true);

      // Upload files first
      const uploadedUrls = await uploadFiles();

      // Update form with new URLs
      let currentHeroImage = basicInfoForm.hero_image;
      if (uploadedUrls.hero_image && uploadedUrls.hero_image.length > 0) {
        currentHeroImage = uploadedUrls.hero_image[0];
      }

      let currentGallery = [...basicInfoForm.gallery_images];
      if (uploadedUrls.gallery_images && uploadedUrls.gallery_images.length > 0) {
        // Append new images to existing gallery
        currentGallery = [...currentGallery, ...uploadedUrls.gallery_images];
      }

      // Also check banners if we decide to convert them too (currently custom array)

      const cleanedMandates = basicInfoForm.mandates.filter(m => m.trim() !== '');
      const cleanedBanners = basicInfoForm.hero_banners.filter(b => b.trim() !== '');
      const cleanedGallery = currentGallery.filter(g => g && g.trim() !== '');
      const cleanedActivities = basicInfoForm.key_activities.filter(a => a.trim() !== '');
      const cleanedStats = basicInfoForm.statistics.filter(s => s.label.trim() !== '' && s.value.trim() !== '');

      const data = {
        type: instituteType,
        name: basicInfoForm.name,
        short_name: basicInfoForm.short_name,
        hero_image: currentHeroImage,
        about_description: basicInfoForm.about_description,
        mandates: cleanedMandates,
        statistics: cleanedStats,
        contact_address: basicInfoForm.contact_address,
        contact_phone: basicInfoForm.contact_phone,
        contact_email: basicInfoForm.contact_email,
        key_activities: cleanedActivities,
        hero_banners: cleanedBanners,
        gallery_images: cleanedGallery,
        linkages_content: basicInfoForm.linkages_content,
        linkages_logos: basicInfoForm.linkages_logos
      };

      await axios.post(`${API_URL}/api/institutes`, data, { headers: getAuthHeaders() });

      toast.success('Basic information saved successfully');
      setFileData({}); // Clear file data after success
      fetchData();
      setSaving(false);
    } catch (error) {
      console.error('Error saving basic info:', error);
      toast.error('Failed to save basic information');
      setSaving(false);
    }
  };

  const saveProject = async () => {
    try {
      setSaving(true);

      if (editingId) {
        await axios.put(
          `${API_URL}/api/institutes/projects/${editingId}`,
          projectForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Project updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/institutes/${instituteType}/projects`,
          projectForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Project created successfully');
      }

      setProjectForm({ title: '', funding_agency: '', duration: '', status: 'ongoing', serial_number: 0 });
      setEditingId(null);
      setOpenDialog(null);
      fetchData();
      setSaving(false);
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
      setSaving(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${API_URL}/api/institutes/projects/${id}`, { headers: getAuthHeaders() });
      toast.success('Project deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const saveActivity = async () => {
    try {
      setSaving(true);

      if (editingId) {
        await axios.put(
          `${API_URL}/api/institutes/activities/${editingId}`,
          activityForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Activity updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/institutes/${instituteType}/activities`,
          activityForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Activity created successfully');
      }

      setActivityForm({ title: '', description: '', activity_date: '', image_url: '' });
      setEditingId(null);
      setOpenDialog(null);
      fetchData();
      setSaving(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      toast.error('Failed to save activity');
      setSaving(false);
    }
  };

  const deleteActivity = async (id) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      await axios.delete(`${API_URL}/api/institutes/activities/${id}`, { headers: getAuthHeaders() });
      toast.success('Activity deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Failed to delete activity');
    }
  };

  const saveAchievement = async () => {
    try {
      setSaving(true);

      if (editingId) {
        await axios.put(
          `${API_URL}/api/institutes/achievements/${editingId}`,
          achievementForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Achievement updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/institutes/${instituteType}/achievements`,
          achievementForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Achievement created successfully');
      }

      setAchievementForm({ title: '', description: '', year: '', image_url: '' });
      setEditingId(null);
      setOpenDialog(null);
      fetchData();
      setSaving(false);
    } catch (error) {
      console.error('Error saving achievement:', error);
      toast.error('Failed to save achievement');
      setSaving(false);
    }
  };

  const deleteAchievement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this achievement?')) return;

    try {
      await axios.delete(`${API_URL}/api/institutes/achievements/${id}`, { headers: getAuthHeaders() });
      toast.success('Achievement deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting achievement:', error);
      toast.error('Failed to delete achievement');
    }
  };

  const addMandateField = () => {
    setBasicInfoForm(prev => ({
      ...prev,
      mandates: [...prev.mandates, '']
    }));
  };

  const updateMandate = (index, value) => {
    const newMandates = [...basicInfoForm.mandates];
    newMandates[index] = value;
    setBasicInfoForm(prev => ({ ...prev, mandates: newMandates }));
  };

  const removeMandate = (index) => {
    const newMandates = basicInfoForm.mandates.filter((_, i) => i !== index);
    setBasicInfoForm(prev => ({ ...prev, mandates: newMandates }));
  };

  const addBannerField = () => {
    setBasicInfoForm(prev => ({
      ...prev,
      hero_banners: [...prev.hero_banners, '']
    }));
  };

  const updateBanner = (index, value) => {
    const newBanners = [...basicInfoForm.hero_banners];
    newBanners[index] = value;
    setBasicInfoForm(prev => ({ ...prev, hero_banners: newBanners }));
  };

  const removeBanner = (index) => {
    const newBanners = basicInfoForm.hero_banners.filter((_, i) => i !== index);
    setBasicInfoForm(prev => ({ ...prev, hero_banners: newBanners }));
  };

  const addGalleryField = () => {
    setBasicInfoForm(prev => ({
      ...prev,
      gallery_images: [...prev.gallery_images, '']
    }));
  };

  const updateGalleryImage = (index, value) => {
    const newImages = [...basicInfoForm.gallery_images];
    newImages[index] = value;
    setBasicInfoForm(prev => ({ ...prev, gallery_images: newImages }));
  };

  const removeGalleryImage = (index) => {
    const newImages = basicInfoForm.gallery_images.filter((_, i) => i !== index);
    setBasicInfoForm(prev => ({ ...prev, gallery_images: newImages }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-[#0E6D2B]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{instituteName} Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage content for <span className="font-semibold text-[#0E6D2B]">{instituteName}</span> only.
              Changes will only affect the <span className="font-semibold">{instituteType.toUpperCase()}</span> page.
            </p>
          </div>
        </div>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="projects">Projects ({projects.length})</TabsTrigger>
            <TabsTrigger value="activities">Activities ({activities.length})</TabsTrigger>
            <TabsTrigger value="achievements">Achievements ({achievements.length})</TabsTrigger>
            <TabsTrigger value="linkages">Linkages</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> You are editing <strong>{instituteName}</strong>.
                All changes below will only affect the <strong>/{instituteType}-malda</strong> page and will not impact the other institute.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Institute Information</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={basicInfoForm.name}
                    onChange={(e) => setBasicInfoForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Regional Research Station, Malda"
                  />
                </div>

                <div>
                  <Label htmlFor="short_name">Short Name</Label>
                  <Input
                    id="short_name"
                    value={basicInfoForm.short_name}
                    onChange={(e) => setBasicInfoForm(prev => ({ ...prev, short_name: e.target.value }))}
                    placeholder="e.g., RRS Malda"
                  />
                </div>

                <div>
                  <Label htmlFor="about">About Description (HTML supported)</Label>
                  <Textarea
                    id="about"
                    value={basicInfoForm.about_description}
                    onChange={(e) => setBasicInfoForm(prev => ({ ...prev, about_description: e.target.value }))}
                    rows={6}
                    placeholder="Enter description about the institute..."
                  />
                </div>

                {/* Contact Information */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="contact_address">Address</Label>
                      <Textarea
                        id="contact_address"
                        value={basicInfoForm.contact_address}
                        onChange={(e) => setBasicInfoForm(prev => ({ ...prev, contact_address: e.target.value }))}
                        rows={3}
                        placeholder="Enter address..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Phone</Label>
                      <Input
                        id="contact_phone"
                        value={basicInfoForm.contact_phone}
                        onChange={(e) => setBasicInfoForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="e.g., +91-XXXX-XXXXXX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_email">Email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={basicInfoForm.contact_email}
                        onChange={(e) => setBasicInfoForm(prev => ({ ...prev, contact_email: e.target.value }))}
                        placeholder="e.g., kvk.malda@cish.gov.in"
                      />
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label>Statistics Cards (Displayed on {instituteType.toUpperCase()} public page)</Label>
                    <Button type="button" size="sm" onClick={() => setBasicInfoForm(prev => ({
                      ...prev,
                      statistics: [...prev.statistics, { label: '', value: '', icon: 'Users' }]
                    }))}>
                      <Plus className="h-4 w-4 mr-1" /> Add Statistic
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">
                    Add statistics specific to {instituteName} (e.g., Farmers Trained: 5000+, Projects: 10+)
                  </p>
                  {basicInfoForm.statistics.map((stat, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                      <Input
                        className="col-span-5"
                        value={stat.label}
                        onChange={(e) => {
                          const newStats = [...basicInfoForm.statistics];
                          newStats[index].label = e.target.value;
                          setBasicInfoForm(prev => ({ ...prev, statistics: newStats }));
                        }}
                        placeholder="e.g., Farmers Trained"
                      />
                      <Input
                        className="col-span-3"
                        value={stat.value}
                        onChange={(e) => {
                          const newStats = [...basicInfoForm.statistics];
                          newStats[index].value = e.target.value;
                          setBasicInfoForm(prev => ({ ...prev, statistics: newStats }));
                        }}
                        placeholder="e.g., 5000+"
                      />
                      <select
                        className="col-span-3 border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={stat.icon}
                        onChange={(e) => {
                          const newStats = [...basicInfoForm.statistics];
                          newStats[index].icon = e.target.value;
                          setBasicInfoForm(prev => ({ ...prev, statistics: newStats }));
                        }}
                      >
                        <option value="Users">Users</option>
                        <option value="Leaf">Leaf</option>
                        <option value="GraduationCap">GraduationCap</option>
                        <option value="Building2">Building2</option>
                        <option value="TestTube">TestTube</option>
                        <option value="Microscope">Microscope</option>
                      </select>
                      {basicInfoForm.statistics.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="col-span-1"
                          onClick={() => {
                            const newStats = basicInfoForm.statistics.filter((_, i) => i !== index);
                            setBasicInfoForm(prev => ({ ...prev, statistics: newStats }));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Key Activities */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label>Key Activities / Focus Areas</Label>
                    <Button type="button" size="sm" onClick={() => setBasicInfoForm(prev => ({
                      ...prev,
                      key_activities: [...prev.key_activities, '']
                    }))}>
                      <Plus className="h-4 w-4 mr-1" /> Add Activity
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">Add key activities, focus areas, or services provided by this institute</p>
                  {basicInfoForm.key_activities.map((activity, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={activity}
                        onChange={(e) => {
                          const newActivities = [...basicInfoForm.key_activities];
                          newActivities[index] = e.target.value;
                          setBasicInfoForm(prev => ({ ...prev, key_activities: newActivities }));
                        }}
                        placeholder="e.g., Technology assessment and refinement"
                      />
                      {basicInfoForm.key_activities.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => {
                            const newActivities = basicInfoForm.key_activities.filter((_, i) => i !== index);
                            setBasicInfoForm(prev => ({ ...prev, key_activities: newActivities }));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <Label htmlFor="hero_image">Hero Image</Label>
                  <FileUploadInput
                    field={{ name: 'hero_image', type: 'file', fileType: 'image' }}
                    value={fileData.hero_image}
                    existingUrl={basicInfoForm.hero_image}
                    formData={basicInfoForm}
                    setFormData={setBasicInfoForm}
                    onChange={(files) => setFileData({ ...fileData, hero_image: files })}
                    onDeleteServerFile={async (path) => {
                      try {
                        // Just removing from UI is enough as we update the URL list in state
                        // But if we want actual file deletion from server:
                        // Extract ID from path /api/files/ID
                        const id = path.split('/').pop();
                        await axios.delete(`${API_URL}/api/files/${id}`, { headers: getAuthHeaders() });
                        toast.success('File deleted');
                        return true;
                      } catch (e) {
                        console.error(e);
                        return false;
                      }
                    }}
                  />
                  <div className="mt-2 text-xs text-gray-400">
                    Legacy URL: {basicInfoForm.hero_image}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Mandates</Label>
                    <Button type="button" size="sm" onClick={addMandateField}>
                      <Plus className="h-4 w-4 mr-1" /> Add Mandate
                    </Button>
                  </div>
                  {basicInfoForm.mandates.map((mandate, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={mandate}
                        onChange={(e) => updateMandate(index, e.target.value)}
                        placeholder="Enter mandate..."
                      />
                      {basicInfoForm.mandates.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeMandate(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <Label>Hero Banners</Label>
                  <FileUploadInput
                    field={{ name: 'hero_banners', type: 'file', multiple: true, fileType: 'image' }}
                    value={fileData.hero_banners}
                    existingUrl={basicInfoForm.hero_banners}
                    formData={basicInfoForm}
                    setFormData={setBasicInfoForm}
                    onChange={(files) => setFileData({ ...fileData, hero_banners: files })}
                    onDeleteServerFile={async (path) => {
                      const id = path.split('/').pop();
                      if (id) {
                        await axios.delete(`${API_URL}/api/files/${id}`, { headers: getAuthHeaders() });
                        return true;
                      }
                      return false;
                    }}
                  />
                </div>

                <div>
                  <Label>Gallery Images</Label>
                  <FileUploadInput
                    field={{ name: 'gallery_images', type: 'file', multiple: true, fileType: 'image' }}
                    value={fileData.gallery_images}
                    existingUrl={basicInfoForm.gallery_images}
                    formData={basicInfoForm}
                    setFormData={setBasicInfoForm}
                    onChange={(files) => setFileData({ ...fileData, gallery_images: files })}
                    onDeleteServerFile={async (path) => {
                      const id = path.split('/').pop();
                      if (id) {
                        await axios.delete(`${API_URL}/api/files/${id}`, { headers: getAuthHeaders() });
                        return true;
                      }
                      return false;
                    }}
                  />
                </div>

                <Button onClick={saveBasicInfo} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Basic Information
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={openDialog === 'project'} onOpenChange={(open) => setOpenDialog(open ? 'project' : null)}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setEditingId(null); setProjectForm({ title: '', funding_agency: '', duration: '', status: 'ongoing', serial_number: 0 }); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit Project' : 'Add New Project'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="proj_title">Project Title</Label>
                      <Input
                        id="proj_title"
                        value={projectForm.title}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Enter project title..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="funding">Funding Agency</Label>
                      <Input
                        id="funding"
                        value={projectForm.funding_agency}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, funding_agency: e.target.value }))}
                        placeholder="Enter funding agency..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration">Duration</Label>
                      <Input
                        id="duration"
                        value={projectForm.duration}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, duration: e.target.value }))}
                        placeholder="e.g., 2020-2023"
                      />
                    </div>
                    <div>
                      <Label htmlFor="serial">Serial Number</Label>
                      <Input
                        id="serial"
                        type="number"
                        value={projectForm.serial_number}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, serial_number: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        value={projectForm.status}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="ongoing">Ongoing</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <Button onClick={saveProject} disabled={saving} className="w-full">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Project
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No.</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Funding Agency</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Duration</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {projects.map((project, index) => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{project.serial_number || index + 1}</td>
                      <td className="px-4 py-3 text-sm">{project.title}</td>
                      <td className="px-4 py-3 text-sm">{project.funding_agency}</td>
                      <td className="px-4 py-3 text-sm">{project.duration}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${project.status === 'ongoing' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(project.id);
                            setProjectForm(project);
                            setOpenDialog('project');
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteProject(project.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={openDialog === 'activity'} onOpenChange={(open) => setOpenDialog(open ? 'activity' : null)}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setEditingId(null); setActivityForm({ title: '', description: '', activity_date: '', image_url: '' }); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Activity
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit Activity' : 'Add New Activity'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="act_title">Title</Label>
                      <Input
                        id="act_title"
                        value={activityForm.title}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="act_desc">Description</Label>
                      <Textarea
                        id="act_desc"
                        value={activityForm.description}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label htmlFor="act_date">Activity Date</Label>
                      <Input
                        id="act_date"
                        value={activityForm.activity_date}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, activity_date: e.target.value }))}
                        placeholder="e.g., March 2025"
                      />
                    </div>
                    <div>
                      <Label htmlFor="act_img">Image URL</Label>
                      <Input
                        id="act_img"
                        value={activityForm.image_url}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, image_url: e.target.value }))}
                      />
                    </div>
                    <Button onClick={saveActivity} disabled={saving} className="w-full">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Activity
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activities.map((activity) => (
                <div key={activity.id} className="bg-white border rounded-lg p-4">
                  {activity.image_url && (
                    <img src={activity.image_url} alt={activity.title} className="w-full h-40 object-cover rounded mb-3" />
                  )}
                  <h3 className="font-semibold text-lg mb-2">{activity.title}</h3>
                  {activity.activity_date && <p className="text-sm text-[#0E6D2B] mb-2">{activity.activity_date}</p>}
                  <p className="text-sm text-gray-600 mb-3">{activity.description.substring(0, 100)}...</p>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingId(activity.id);
                        setActivityForm(activity);
                        setOpenDialog('activity');
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteActivity(activity.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={openDialog === 'achievement'} onOpenChange={(open) => setOpenDialog(open ? 'achievement' : null)}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setEditingId(null); setAchievementForm({ title: '', description: '', year: '', image_url: '' }); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Achievement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit Achievement' : 'Add New Achievement'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="ach_title">Title</Label>
                      <Input
                        id="ach_title"
                        value={achievementForm.title}
                        onChange={(e) => setAchievementForm(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ach_desc">Description</Label>
                      <Textarea
                        id="ach_desc"
                        value={achievementForm.description}
                        onChange={(e) => setAchievementForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label htmlFor="ach_year">Year</Label>
                      <Input
                        id="ach_year"
                        value={achievementForm.year}
                        onChange={(e) => setAchievementForm(prev => ({ ...prev, year: e.target.value }))}
                        placeholder="e.g., 2024"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ach_img">Image URL</Label>
                      <Input
                        id="ach_img"
                        value={achievementForm.image_url}
                        onChange={(e) => setAchievementForm(prev => ({ ...prev, image_url: e.target.value }))}
                      />
                    </div>
                    <Button onClick={saveAchievement} disabled={saving} className="w-full">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Achievement
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {achievements.map((achievement) => (
                <div key={achievement.id} className="bg-white border rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    {achievement.image_url && (
                      <img src={achievement.image_url} alt={achievement.title} className="w-32 h-32 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-xl mb-2">{achievement.title}</h3>
                      {achievement.year && <p className="text-sm text-[#0E6D2B] font-medium mb-2">Year: {achievement.year}</p>}
                      <p className="text-gray-700 mb-3">{achievement.description}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingId(achievement.id);
                            setAchievementForm(achievement);
                            setOpenDialog('achievement');
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteAchievement(achievement.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Linkages Tab */}
          <TabsContent value="linkages" className="space-y-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Linkages & Collaborations</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="linkages_content">Content (HTML supported)</Label>
                  <Textarea
                    id="linkages_content"
                    value={basicInfoForm.linkages_content}
                    onChange={(e) => setBasicInfoForm(prev => ({ ...prev, linkages_content: e.target.value }))}
                    rows={6}
                    placeholder="Enter linkages and collaborations content..."
                  />
                </div>

                <Button onClick={saveBasicInfo} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Linkages
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default InstituteManagementPage;
